'use client';
import { useCallback, useEffect, useRef, useState } from 'react';

type RangeSliderProps = {
  min: number;
  max: number;
  step: number;
  onChange: (value: number | [number, number]) => void;
  onChangeComplete?: (value: number | [number, number]) => void;
  salaryRange?: [number, number];
};

function clamp(n: number, lo: number, hi: number) {
  return Math.min(Math.max(n, lo), hi);
}

export default function RangeSlider({
  min,
  max,
  step,
  onChange,
  onChangeComplete,
  salaryRange,
}: RangeSliderProps) {
  const [values, setValues] = useState<[number, number]>([min, max]);
  const [activeThumb, setActiveThumb] = useState<0 | 1 | null>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const activeThumbRef = useRef<0 | 1 | null>(null);
  const valuesRef = useRef(values);
  const span = max - min || 1;

  useEffect(() => {
    if (!salaryRange) return;
    setValues(salaryRange);
  }, [salaryRange]);

  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const valueFromClientX = useCallback(
    (clientX: number) => {
      const track = trackRef.current;
      if (!track) return min;
      const rect = track.getBoundingClientRect();
      const ratio = rect.width === 0 ? 0 : (clientX - rect.left) / rect.width;
      const raw = min + clamp(ratio, 0, 1) * (max - min);
      const snapped = Math.round((raw - min) / step) * step + min;
      return clamp(snapped, min, max);
    },
    [min, max, step]
  );

  const commit = useCallback(
    (next: [number, number]) => {
      valuesRef.current = next;
      setValues(next);
      onChange(next);
    },
    [onChange]
  );

  const applyPointer = useCallback(
    (clientX: number, thumb: 0 | 1) => {
      const nextValue = valueFromClientX(clientX);
      const [minVal, maxVal] = valuesRef.current;

      if (thumb === 0) {
        if (nextValue > maxVal) {
          activeThumbRef.current = 1;
          setActiveThumb(1);
          commit([maxVal, nextValue]);
        } else {
          commit([nextValue, maxVal]);
        }
      } else if (nextValue < minVal) {
        activeThumbRef.current = 0;
        setActiveThumb(0);
        commit([nextValue, minVal]);
      } else {
        commit([minVal, nextValue]);
      }
    },
    [commit, valueFromClientX]
  );

  const nearestThumb = useCallback(
    (clientX: number): 0 | 1 => {
      const v = valueFromClientX(clientX);
      const [minVal, maxVal] = valuesRef.current;
      return Math.abs(v - minVal) <= Math.abs(v - maxVal) ? 0 : 1;
    },
    [valueFromClientX]
  );

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (activeThumbRef.current === null) return;
      applyPointer(e.clientX, activeThumbRef.current);
    };
    const onUp = () => {
      if (activeThumbRef.current === null) return;
      activeThumbRef.current = null;
      setActiveThumb(null);
      onChangeComplete?.(valuesRef.current);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
    };
  }, [applyPointer, onChangeComplete]);

  const startDrag = (thumb: 0 | 1, clientX: number) => {
    activeThumbRef.current = thumb;
    setActiveThumb(thumb);
    applyPointer(clientX, thumb);
  };

  const minPercent = ((values[0] - min) / span) * 100;
  const maxPercent = ((values[1] - min) / span) * 100;

  const onKeyDown = (thumb: 0 | 1) => (e: React.KeyboardEvent) => {
    let delta = 0;
    if (e.key === 'ArrowLeft' || e.key === 'ArrowDown') delta = -step;
    else if (e.key === 'ArrowRight' || e.key === 'ArrowUp') delta = step;
    else if (e.key === 'Home') delta = min - (thumb === 0 ? values[0] : values[1]);
    else if (e.key === 'End') delta = max - (thumb === 0 ? values[0] : values[1]);
    else return;

    e.preventDefault();
    const [minVal, maxVal] = values;
    const next: [number, number] =
      thumb === 0
        ? [clamp(minVal + delta, min, maxVal), maxVal]
        : [minVal, clamp(maxVal + delta, minVal, max)];
    commit(next);
    onChangeComplete?.(next);
  };

  return (
    <div className="custom-slider w-full min-w-0 mx-auto px-2 overflow-hidden">
      <div
        ref={trackRef}
        className="relative h-4 flex items-center select-none touch-none cursor-pointer"
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          startDrag(nearestThumb(e.clientX), e.clientX);
        }}
      >
        <div className="absolute inset-x-0 h-1 bg-gray-200 rounded-full" />
        <div
          className="absolute h-1 bg-primary-900 rounded-full"
          style={{
            left: `${minPercent}%`,
            right: `${100 - maxPercent}%`,
          }}
        />
        <div
          role="slider"
          tabIndex={0}
          aria-label="Minimum"
          aria-valuemin={min}
          aria-valuemax={values[1]}
          aria-valuenow={values[0]}
          className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-900 outline-none cursor-grab active:cursor-grabbing ${
            activeThumb === 0 ? 'z-30' : 'z-20'
          }`}
          style={{ left: `${minPercent}%` }}
          onPointerDown={(e) => {
            e.stopPropagation();
            if (e.button !== 0) return;
            e.preventDefault();
            startDrag(0, e.clientX);
          }}
          onKeyDown={onKeyDown(0)}
        />
        <div
          role="slider"
          tabIndex={0}
          aria-label="Maximum"
          aria-valuemin={values[0]}
          aria-valuemax={max}
          aria-valuenow={values[1]}
          className={`absolute top-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary-900 outline-none cursor-grab active:cursor-grabbing ${
            activeThumb === 1 ? 'z-30' : 'z-20'
          }`}
          style={{ left: `${maxPercent}%` }}
          onPointerDown={(e) => {
            e.stopPropagation();
            if (e.button !== 0) return;
            e.preventDefault();
            startDrag(1, e.clientX);
          }}
          onKeyDown={onKeyDown(1)}
        />
      </div>
    </div>
  );
}
